import { type FC, type RefObject, useEffect, useState } from 'react'
import classNames from 'classnames'
import { type ScoreRow } from '../../../components/Insights/scoreRoster'
import styles from './UserScorePager.module.scss'

interface UserScorePagerProps {
  previous: ScoreRow | null
  next: ScoreRow | null
  position: number
  total: number
  hidden?: boolean
  // The pager centers over this element (the page content area) instead of the viewport
  anchorRef?: RefObject<HTMLElement | null>
  onNavigate: (row: ScoreRow) => void
}

const isTypingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)

const pagerLabel = (row: ScoreRow | null, fallback: string): string =>
  row ? `${row.name}${row.score !== null ? ` (${Math.round(row.score)})` : ''}` : fallback

export const UserScorePager: FC<UserScorePagerProps> = ({ previous, next, position, total, hidden, anchorRef, onNavigate }) => {
  const [anchor, setAnchor] = useState<{ left: number; width: number } | null>(null)

  useEffect(() => {
    const element = anchorRef?.current
    if (!element) {
      return
    }
    const measure = () => {
      const rect = element.getBoundingClientRect()
      setAnchor({ left: rect.left, width: rect.width })
    }
    measure()
    // Not every environment implements ResizeObserver; the window resize listener below still keeps the pager aligned
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(element)
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [anchorRef])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hidden || event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
        return
      }
      if (event.key === 'ArrowLeft' && previous) {
        onNavigate(previous)
      }
      if (event.key === 'ArrowRight' && next) {
        onNavigate(next)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previous, next, hidden, onNavigate])

  return (
    <div
      className={classNames(styles.pagerContainer, hidden ? styles.pagerContainerHidden : null)}
      style={anchor ? { left: anchor.left, width: anchor.width, right: 'auto' } : undefined}
    >
      <div className={styles.pager}>
        <button
          type='button'
          className={classNames(styles.pagerButton, styles.pagerButtonPrev)}
          disabled={!previous}
          onClick={() => previous && onNavigate(previous)}
        >
          <i className='bi bi-chevron-left' />
          {pagerLabel(previous, 'Previous')}
        </button>
        <span className={classNames(styles.pagerPosition, styles.tabularNums)}>
          {position} of {total}
        </span>
        <button type='button' className={classNames(styles.pagerButton, styles.pagerButtonNext)} disabled={!next} onClick={() => next && onNavigate(next)}>
          {pagerLabel(next, 'Next')}
          <i className='bi bi-chevron-right' />
        </button>
      </div>
    </div>
  )
}
